import React from 'react';
import {
  Autocomplete, Box, Chip, CircularProgress, FormControl, IconButton, MenuItem, Select, TextField, Tooltip, Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import SpeedIcon from '@mui/icons-material/Speed';
import { adminApi } from '../../api';

const COLORS = {
  metadata: '#46d7b0',
  layout: '#ffd166',
  requestPath: '#5aa9ff',
  browser: '#b597ff',
  unresolved: '#244966',
  marker: '#6eefff',
};

const formatMs = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const ms = Number(value);
  return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(2)} s`;
};

const formatBytes = (value) => {
  if (!Number.isFinite(Number(value))) return '—';
  return `${(Number(value) / 1000000).toFixed(1)} MB`;
};

const diagnosisPresentation = (code) => ({
  healthy: { label: 'HEALTHY', color: '#46d7b0' },
  kairos: { label: 'KAIROS-SIDE', color: '#ff8f5c' },
  request_path: { label: 'NETWORK / REQUEST PATH', color: '#5aa9ff' },
  workstation: { label: 'CLIENT-SIDE', color: '#b597ff' },
  mixed: { label: 'INSUFFICIENT / MIXED', color: '#ffd166' },
}[code] || { label: 'INSUFFICIENT DATA', color: '#78909c' });

const interactionPresentation = (interaction = {}) => {
  if (interaction.state === 'not_observed' || !interaction.summary) return { label: 'SCROLL NOT OBSERVED', color: '#78909c' };
  if (interaction.state === 'partial') {
    return interaction.status === 'degraded'
      ? { label: 'SCROLL PARTIAL · DEGRADED', color: '#ff8f5c' }
      : { label: 'SCROLL PARTIAL', color: '#ffd166' };
  }
  return interaction.status === 'degraded'
    ? { label: 'SCROLL DEGRADED', color: '#ff5f6d' }
    : { label: 'SCROLL OBSERVED', color: '#46d7b0' };
};

const MetricCard = ({ label, value, accent = '#6eefff', caption }) => (
  <Box sx={{ minWidth: 160, flex: '1 1 180px', p: 1.5, border: `1px solid ${accent}28`, borderRadius: 2,
    background: `linear-gradient(145deg, ${accent}12, rgba(5,10,14,0.92))`, boxShadow: 'inset 0 1px rgba(255,255,255,0.025)' }}>
    <Typography sx={{ fontSize: 9, color: '#6f8794', letterSpacing: 1.4, fontFamily: 'monospace' }}>{label}</Typography>
    <Typography sx={{ mt: 0.4, fontSize: 22, lineHeight: 1.1, color: accent, fontWeight: 800, fontFamily: 'monospace' }}>{value}</Typography>
    {caption && <Typography sx={{ mt: 0.5, fontSize: 9, color: '#536772', fontFamily: 'monospace' }}>{caption}</Typography>}
  </Box>
);

const Legend = () => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.4, alignItems: 'center' }}>
    {[
      ['METADATA MILESTONE', COLORS.metadata], ['LAYOUT MILESTONE', COLORS.layout], ['ELAPSED OPEN WINDOW', COLORS.unresolved],
      ['DIAGNOSTIC PIXELS', COLORS.marker],
    ].map(([label, color]) => (
      <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: label === 'DIAGNOSTIC PIXELS' ? 2 : 8, height: 8, borderRadius: 0.5, bgcolor: color, boxShadow: `0 0 8px ${color}55` }} />
        <Typography sx={{ fontSize: 8.5, color: '#78909c', letterSpacing: 0.7, fontFamily: 'monospace' }}>{label}</Typography>
      </Box>
    ))}
  </Box>
);

const StudyOpenTimeline = ({ item, maxMs }) => {
  const total = Math.max(1, Number(item.interactive_ms || item.first_image_ms || 1));
  const scale = Math.max(total, maxMs || total);
  const metadata = Math.min(Number(item.metadata_ms || 0), scale);
  const layoutEnd = Math.min(Number(item.layout_ms || metadata), scale);
  const firstImage = Math.min(Number(item.first_image_ms || layoutEnd), scale);
  const markers = [
    { key: 'metadata', at: metadata, color: COLORS.metadata, label: 'Metadata milestone' },
    { key: 'layout', at: layoutEnd, color: COLORS.layout, label: 'Layout milestone' },
  ];
  return (
    <Box aria-label="Study open performance timeline" sx={{ height: 24, position: 'relative', overflow: 'hidden', borderRadius: 1,
      bgcolor: '#0b1116', border: '1px solid rgba(110,239,255,0.08)' }}>
      <Box title={`Elapsed open window: ${formatMs(firstImage)}`} sx={{ position: 'absolute', left: 0,
        width: `${Math.max(0.35, (firstImage / scale) * 100)}%`, top: 8, height: 8, bgcolor: COLORS.unresolved,
        borderRadius: 0.6, opacity: 0.55 }} />
      {markers.filter(marker => marker.at > 0).map(marker => (
        <Box key={marker.key} title={`${marker.label}: ${formatMs(marker.at)}`} sx={{ position: 'absolute', left: `${(marker.at / scale) * 100}%`,
          top: 4, bottom: 4, width: 2, bgcolor: marker.color, boxShadow: `0 0 7px ${marker.color}88` }} />
      ))}
      {item.first_image_ms != null && <Box title={`Diagnostic pixels: ${formatMs(item.first_image_ms)}`} sx={{ position: 'absolute', left: `${(firstImage / scale) * 100}%`,
        top: 1, bottom: 1, width: 2, bgcolor: COLORS.marker, boxShadow: `0 0 9px ${COLORS.marker}` }} />}
    </Box>
  );
};

const InteractionDetails = ({ interaction = {} }) => {
  const summary = interaction.summary;
  if (!summary) {
    return <Typography sx={{ px: 1.5, py: 1, color: '#647985', fontSize: 9, fontFamily: 'monospace', borderTop: '1px solid rgba(110,239,255,0.07)' }}>
      SCROLL NOT OBSERVED · The initial-open result does not describe subsequent navigation.
    </Typography>;
  }
  const samples = Number(summary.accept_latency_sample_count || 0);
  const average = samples ? Number(summary.accept_latency_total_ms || 0) / samples : 0;
  return <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid rgba(110,239,255,0.07)' }}>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
      {[
        `SCROLL ${String(interaction.state || 'partial').toUpperCase()}`,
        `INPUTS ${summary.wheel_input_count || 0}`,
        `TARGETS ${summary.visible_target_count || 0}`,
        `ACCEPTED ${summary.accepted_visible_count || 0}`,
        `UNACCEPTED ${summary.unaccepted_target_count || 0}`,
        `COALESCED SKIP SLICES ${summary.coalesced_skip_slice_count || 0}`,
        `ACCEPTED GAP SLICES ${summary.accepted_gap_slice_count || 0}`,
        `DECODED CACHE ${summary.decoded_cache_hit_count || 0} HIT / ${summary.decoded_cache_miss_count || 0} MISS`,
        `INPUT→ACCEPTED AVG ${formatMs(average)} / MAX ${formatMs(summary.accept_latency_max_ms)} / ${samples} SAMPLES`,
      ].map(text => <Typography key={text} sx={{ fontSize: 8.5, color: '#8aa1ac', fontFamily: 'monospace' }}>{text}</Typography>)}
    </Box>
    <Typography sx={{ mt: 0.8, fontSize: 8, color: '#536772', fontFamily: 'monospace' }}>
      Skip/gap counts describe slice transitions, not proof of blank frames, stale paint, or dropped display. Cache counts cover only this page’s decoded cache. Latency ends at accepted-visible publication.
    </Typography>
  </Box>;
};

const SeriesDetails = ({ item, maxMs }) => (
  <Box sx={{ mx: 1.5, mb: 1.2, border: '1px solid rgba(110,239,255,0.08)', borderTop: 0, borderRadius: '0 0 10px 10px', bgcolor: 'rgba(4,9,13,0.82)' }}>
    {(item.series || []).length === 0 ? (
      <Typography sx={{ p: 1.5, color: '#536772', fontSize: 10, fontFamily: 'monospace' }}>No per-series accepted-pixel sample was retained.</Typography>
    ) : item.series.map(series => (
      <Box key={series.ordinal} sx={{ display: 'grid', gridTemplateColumns: '90px 56px 105px minmax(160px,1fr) 110px', gap: 1, px: 1.5, py: 1,
        alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.035)' }}>
        <Typography sx={{ color: '#d7fbff', fontSize: 10, fontWeight: 700, fontFamily: 'monospace' }}>SERIES {series.ordinal}</Typography>
        <Typography sx={{ color: '#8aa1ac', fontSize: 9, fontFamily: 'monospace' }}>{series.modality}</Typography>
        <Typography sx={{ color: '#8aa1ac', fontSize: 9, fontFamily: 'monospace' }}>{series.instance_count} INSTANCES</Typography>
        <Box sx={{ position: 'relative', height: 8, bgcolor: '#0b1116', borderRadius: 4, overflow: 'hidden' }}>
          <Box sx={{ width: `${Math.min(100, (Number(series.first_visible_ms || 0) / Math.max(1, maxMs)) * 100)}%`, height: '100%', bgcolor: COLORS.marker, opacity: 0.72 }} />
        </Box>
        <Typography sx={{ color: '#6eefff', fontSize: 9, fontFamily: 'monospace' }}>VISIBLE {formatMs(series.first_visible_ms)}</Typography>
      </Box>
    ))}
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, px: 1.5, py: 1, borderTop: '1px solid rgba(110,239,255,0.07)' }}>
      {[
        `OUTCOME ${String(item.outcome || 'unknown').toUpperCase()}`,
        `ACCEPTED ${item.painted_viewport_count || 0}/${item.viewport_count || 0}`,
        `REQUESTS ${item.request_count || 0}`, `SLOWEST REQUEST ${formatMs(item.request_path_ms)}`,
        `KAIROS SERVER-TIMING ${Number(item.kairos_ms || 0) > 0 ? formatMs(item.kairos_ms) : 'NOT EXPOSED'}`,
        `BROWSER STALLS ${formatMs(item.long_task_ms)}`, `TRANSFER ${formatBytes(item.transfer_bytes)}`,
        `BROWSER ${String(item.browser_engine || 'unknown').toUpperCase()}`,
        `CONNECTION ${String(item.connection_class || 'unknown').toUpperCase()}`,
      ].map(text => <Typography key={text} sx={{ fontSize: 8.5, color: '#647985', fontFamily: 'monospace' }}>{text}</Typography>)}
    </Box>
    <InteractionDetails interaction={item.interaction} />
  </Box>
);

const StudyPerformanceMonitoring = ({
  loadPerformance = adminApi.getStudyPerformance,
  loadUserPerformance = adminApi.queryStudyPerformance,
  searchUsers = adminApi.searchStudyPerformanceUsers,
  allowUserFilter = false,
  autoRefreshMs = 30000,
}) => {
  const [hours, setHours] = React.useState(24);
  const [modality, setModality] = React.useState('');
  const [diagnosis, setDiagnosis] = React.useState('');
  const [trace, setTrace] = React.useState('');
  const [debouncedTrace, setDebouncedTrace] = React.useState('');
  const [data, setData] = React.useState({ summary: {}, items: [] });
  const [dataScopeKey, setDataScopeKey] = React.useState('global');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [expanded, setExpanded] = React.useState(new Set());
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [userSearch, setUserSearch] = React.useState('');
  const [userOptions, setUserOptions] = React.useState([]);
  const [userSearchLoading, setUserSearchLoading] = React.useState(false);
  const [userSearchError, setUserSearchError] = React.useState('');
  const refreshGenerationRef = React.useRef(0);
  const userSearchGenerationRef = React.useRef(0);
  const mountedRef = React.useRef(true);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      refreshGenerationRef.current += 1;
      userSearchGenerationRef.current += 1;
    };
  }, []);
  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedTrace(trace), 250);
    return () => window.clearTimeout(timer);
  }, [trace]);

  React.useEffect(() => {
    const query = userSearch.trim();
    if (!allowUserFilter || query.length < 2) {
      userSearchGenerationRef.current += 1;
      setUserOptions([]);
      setUserSearchLoading(false);
      setUserSearchError('');
      return undefined;
    }
    const generation = ++userSearchGenerationRef.current;
    setUserOptions([]);
    setUserSearchError('');
    setUserSearchLoading(true);
    const timer = window.setTimeout(() => {
      Promise.resolve(searchUsers(query, 10))
        .then(result => {
          if (!mountedRef.current || generation !== userSearchGenerationRef.current) return;
          const users = (result?.users || []).map(user => ({
            id: String(user.id || ''),
            username: String(user.username || ''),
            is_active: user.is_active !== false,
          })).filter(user => user.id && user.username);
          setUserOptions(users);
          setUserSearchError('');
        })
        .catch(requestError => {
          if (!mountedRef.current || generation !== userSearchGenerationRef.current) return;
          setUserOptions([]);
          setUserSearchError(requestError?.response?.data?.detail || requestError?.message || 'User search unavailable.');
        })
        .finally(() => {
          if (mountedRef.current && generation === userSearchGenerationRef.current) setUserSearchLoading(false);
        });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [allowUserFilter, searchUsers, userSearch]);

  const refresh = React.useCallback(async () => {
    const generation = ++refreshGenerationRef.current;
    setLoading(true);
    setError('');
    try {
      const params = { hours, limit: 100 };
      if (modality) params.modality = modality;
      if (diagnosis) params.diagnosis = diagnosis;
      if (debouncedTrace.length >= 4) params.trace = debouncedTrace;
      const result = selectedUser?.id
        ? await loadUserPerformance({ ...params, user_id: selectedUser.id })
        : await loadPerformance(params);
      if (!mountedRef.current || generation !== refreshGenerationRef.current) return;
      setData(result);
      setDataScopeKey(selectedUser?.id || 'global');
      setError('');
    } catch (requestError) {
      if (!mountedRef.current || generation !== refreshGenerationRef.current) return;
      setError(requestError?.response?.data?.detail || requestError?.message || 'Study performance telemetry is unavailable.');
    } finally {
      if (mountedRef.current && generation === refreshGenerationRef.current) setLoading(false);
    }
  }, [debouncedTrace, diagnosis, hours, loadPerformance, loadUserPerformance, modality, selectedUser]);

  React.useEffect(() => { refresh(); }, [refresh]);
  React.useEffect(() => {
    if (!autoRefreshMs) return undefined;
    const timer = window.setInterval(refresh, autoRefreshMs);
    return () => window.clearInterval(timer);
  }, [autoRefreshMs, refresh]);

  const currentScopeKey = selectedUser?.id || 'global';
  const visibleData = dataScopeKey === currentScopeKey ? data : { summary: {}, items: [] };
  const items = visibleData?.items || [];
  const summary = visibleData?.summary || {};
  const maxMs = Math.max(1000, ...items.map(item => Number(item.interactive_ms || item.first_image_ms || 0)));
  const toggleExpanded = (eventId) => setExpanded(previous => {
    const next = new Set(previous);
    if (next.has(eventId)) next.delete(eventId); else next.add(eventId);
    return next;
  });

  return (
    <Box data-kairos-study-performance="PHI_SAFE_ACCEPTED_PIXEL_TELEMETRY" sx={{ color: '#d7fbff' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 15, color: '#d7fbff', fontWeight: 800, letterSpacing: 1.4 }}>STUDY PERFORMANCE</Typography>
          <Typography sx={{ mt: 0.4, fontSize: 9.5, color: '#5f7783', fontFamily: 'monospace' }}>Accepted diagnostic pixels · bounded browser spans · 14-day retention · no study, patient, raw user, session, URL, or IP identifiers</Typography>
        </Box>
        <Tooltip title="Refresh performance telemetry">
          <span><IconButton size="small" onClick={refresh} disabled={loading} aria-label="Refresh performance telemetry" sx={{ color: '#6eefff', border: '1px solid rgba(110,239,255,0.18)' }}>
            {loading ? <CircularProgress size={15} color="inherit" /> : <RefreshIcon sx={{ fontSize: 16 }} />}
          </IconButton></span>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1.2, mb: 1.5, border: '1px solid rgba(110,239,255,0.10)', borderRadius: 2,
        background: 'linear-gradient(145deg, rgba(9,20,28,0.86), rgba(4,8,12,0.92))' }}>
        <FormControl size="small"><Select aria-label="Performance time window" value={hours} onChange={event => setHours(Number(event.target.value))} sx={{ minWidth: 130, height: 30, color: '#b6cbd4', fontSize: 10, fontFamily: 'monospace' }}>
          <MenuItem value={1}>LAST HOUR</MenuItem><MenuItem value={24}>LAST 24 HOURS</MenuItem><MenuItem value={168}>LAST 7 DAYS</MenuItem><MenuItem value={720}>LAST 30 DAYS</MenuItem>
        </Select></FormControl>
        <FormControl size="small"><Select aria-label="Filter modality" value={modality} displayEmpty onChange={event => setModality(event.target.value)} sx={{ minWidth: 125, height: 30, color: '#b6cbd4', fontSize: 10, fontFamily: 'monospace' }}>
          <MenuItem value="">ALL MODALITIES</MenuItem>{['CT', 'MR', 'DX', 'CR', 'MG', 'US', 'NM', 'PT', 'XA', 'RF', 'OT', 'SR'].map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
        </Select></FormControl>
        <FormControl size="small"><Select aria-label="Filter likely bottleneck" value={diagnosis} displayEmpty onChange={event => setDiagnosis(event.target.value)} sx={{ minWidth: 170, height: 30, color: '#b6cbd4', fontSize: 10, fontFamily: 'monospace' }}>
          <MenuItem value="">ALL DIAGNOSES</MenuItem><MenuItem value="healthy">HEALTHY</MenuItem><MenuItem value="kairos">SERVER TIMING</MenuItem><MenuItem value="request_path">SLOW REQUEST PATH</MenuItem><MenuItem value="workstation">BROWSER STALLS</MenuItem><MenuItem value="mixed">INSUFFICIENT / MIXED</MenuItem>
        </Select></FormControl>
        <TextField size="small" inputProps={{ 'aria-label': 'Filter by trace code' }} placeholder="TRACE CODE" value={trace} onChange={event => setTrace(event.target.value.toUpperCase().replace(/[^0-9A-F]/g, '').slice(0, 32))}
          sx={{ width: 145, '& .MuiInputBase-root': { height: 30, color: '#b6cbd4', fontSize: 10, fontFamily: 'monospace' }, '& fieldset': { borderColor: 'rgba(110,239,255,0.18)' } }} />
        {allowUserFilter && <Autocomplete
          size="small"
          options={userOptions}
          value={selectedUser}
          inputValue={userSearch}
          loading={userSearchLoading}
          filterOptions={options => options}
          getOptionLabel={option => option?.username || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onInputChange={(_event, value, reason) => {
            if (reason === 'input' || reason === 'clear') {
              const boundedValue = value.slice(0, 80);
              setUserSearch(boundedValue);
              if (selectedUser && boundedValue !== selectedUser.username) setSelectedUser(null);
            }
          }}
          onChange={(_event, value) => {
            userSearchGenerationRef.current += 1;
            setSelectedUser(value);
            setUserOptions([]);
            setUserSearch(value?.username || '');
            setUserSearchError('');
          }}
          noOptionsText={userSearch.trim().length < 2 ? 'TYPE 2+ CHARACTERS' : 'NO MATCHING USERS'}
          renderOption={(props, option) => <li {...props} key={option.id}>{option.username} · {option.is_active ? 'ACTIVE' : 'INACTIVE'}</li>}
          renderInput={params => <TextField {...params} placeholder="FILTER USER" inputProps={{ ...params.inputProps, 'aria-label': 'Search performance by username' }}
            InputProps={{ ...params.InputProps, endAdornment: <>{userSearchLoading ? <CircularProgress color="inherit" size={13} /> : null}{params.InputProps.endAdornment}</> }} />}
          sx={{ width: 220, '& .MuiInputBase-root': { height: 30, color: '#b6cbd4', fontSize: 10, fontFamily: 'monospace' }, '& fieldset': { borderColor: 'rgba(110,239,255,0.18)' } }}
        />}
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}><SpeedIcon sx={{ color: '#46d7b0', fontSize: 15 }} /><Typography sx={{ color: '#66808c', fontSize: 9, fontFamily: 'monospace' }}>AUTO REFRESH {autoRefreshMs ? `${autoRefreshMs / 1000}s` : 'OFF'}</Typography></Box>
      </Box>

      {allowUserFilter && userSearchError && <Box role="alert" sx={{ p: 1, mb: 1, color: '#ffb3b3', fontSize: 9, fontFamily: 'monospace' }}>{userSearchError}</Box>}
      {selectedUser && <Box sx={{ mb: 1.5, px: 1.2, py: 0.8, borderLeft: '2px solid #6eefff', bgcolor: 'rgba(110,239,255,0.045)' }}>
        <Typography sx={{ color: '#8aa1ac', fontSize: 9, fontFamily: 'monospace' }}>
          USER {selectedUser.username.toUpperCase()} · Results are matched through a pseudonymous server-derived account tag. The browser never submits identity with telemetry.
        </Typography>
      </Box>}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <MetricCard label="RECORDED OPENS" value={summary.opens ?? 0} caption={`${hours}h bounded window`} />
        <MetricCard label="P50 TO DIAGNOSTIC PIXELS" value={formatMs(summary.p50_first_image_ms)} accent="#46d7b0" caption="median accepted first image" />
        <MetricCard label="P95 TO DIAGNOSTIC PIXELS" value={formatMs(summary.p95_first_image_ms)} accent="#ff8f5c" caption="slow-tail accepted first image" />
        <MetricCard label="SERVER-TIMED / REQUEST-PATH / BROWSER" value={`${summary.kairos_count || 0} / ${summary.request_path_count || 0} / ${summary.workstation_count || 0}`} accent="#b597ff" caption="initial-open causal limits" />
        <MetricCard label="SCROLL OBSERVED / DEGRADED" value={`${summary.interaction_observed_count || 0} / ${summary.interaction_degraded_count || 0}`} accent="#ff8f5c" caption="separate from initial-open health" />
      </Box>

      <Box sx={{ mb: 1, px: 1 }}><Legend /></Box>
      {error && <Box role="alert" sx={{ p: 1.5, mb: 1, color: '#ff8f8f', bgcolor: 'rgba(255,79,79,0.08)', border: '1px solid rgba(255,79,79,0.25)', borderRadius: 1 }}>{error}</Box>}
      {!loading && items.length === 0 && !error && <Box sx={{ p: 4, textAlign: 'center', border: '1px dashed rgba(110,239,255,0.12)', borderRadius: 2 }}><Typography sx={{ color: '#5f7783', fontFamily: 'monospace', fontSize: 10 }}>NO STUDY-OPEN SAMPLES IN THIS WINDOW</Typography></Box>}
      <Box sx={{ overflowX: 'auto' }}>
      <Box role="row" sx={{ minWidth: 900, display: 'grid', gridTemplateColumns: '34px 102px 54px 116px minmax(240px,1fr) 82px 82px 165px', gap: 1, px: 1.2, py: 0.7 }}>
        {['', 'OPEN', 'MOD', 'OBSERVED', 'MEASURED MILESTONES', 'FIRST PIXEL', 'OUTCOME', 'EVIDENCE'].map(label => <Typography role="columnheader" key={label} sx={{ color: '#526975', fontSize: 8, fontFamily: 'monospace', letterSpacing: 0.8 }}>{label}</Typography>)}
      </Box>
      {items.map((item, index) => {
        const presentation = diagnosisPresentation(item.diagnosis?.code);
        const scrollPresentation = interactionPresentation(item.interaction);
        const isExpanded = expanded.has(item.event_id);
        return <React.Fragment key={item.event_id}>
          <Box role="row" sx={{ minWidth: 900, display: 'grid', gridTemplateColumns: '34px 102px 54px 116px minmax(240px,1fr) 82px 82px 165px', gap: 1, px: 1.2, py: 1,
            alignItems: 'center', mb: isExpanded ? 0 : 0.8, border: `1px solid ${presentation.color}24`, borderRadius: isExpanded ? '10px 10px 0 0' : 2,
            bgcolor: 'rgba(10,15,19,0.88)', background: 'linear-gradient(100deg, rgba(110,239,255,0.025), rgba(4,7,10,0.92))' }}>
            <IconButton size="small" aria-label={`Expand open ${index + 1} details`} aria-expanded={isExpanded} aria-controls={`study-open-details-${item.event_id}`} onClick={() => toggleExpanded(item.event_id)} sx={{ color: '#6f8794', width: 26, height: 26 }}>
              {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
            </IconButton>
            <Box><Typography sx={{ color: '#d7fbff', fontSize: 9.5, fontFamily: 'monospace', fontWeight: 800 }}>OPEN {String(index + 1).padStart(2, '0')}</Typography><Typography sx={{ color: '#5aa9ff', fontSize: 7.5, fontFamily: 'monospace' }}>TRACE {String(item.event_id || '').slice(0, 8).toUpperCase()}</Typography></Box>
            <Typography sx={{ color: '#9cb0b9', fontSize: 10, fontFamily: 'monospace' }}>{item.modality}</Typography>
            <Typography sx={{ color: '#78909c', fontSize: 8.5, fontFamily: 'monospace' }}>{new Date(item.observed_at).toLocaleString([], { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</Typography>
            <StudyOpenTimeline item={item} maxMs={maxMs} />
            <Typography sx={{ color: '#6eefff', fontSize: 9.5, fontFamily: 'monospace' }}>{formatMs(item.first_image_ms)}</Typography>
            <Typography sx={{ color: '#8aa1ac', fontSize: 9.5, fontFamily: 'monospace' }}>{String(item.outcome || 'unknown').toUpperCase()}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.45 }}>
              <Tooltip title={`Initial open: ${item.diagnosis?.summary || ''} Confidence: ${item.diagnosis?.confidence || 'low'}.`}><Chip size="small" label={`OPEN ${presentation.label}`} sx={{ height: 20, color: presentation.color, bgcolor: `${presentation.color}12`, border: `1px solid ${presentation.color}55`, fontSize: 7.8, fontWeight: 800, fontFamily: 'monospace' }} /></Tooltip>
              <Tooltip title="Scroll interaction is measured separately from initial study open."><Chip size="small" label={scrollPresentation.label} sx={{ height: 20, color: scrollPresentation.color, bgcolor: `${scrollPresentation.color}12`, border: `1px solid ${scrollPresentation.color}55`, fontSize: 7.8, fontWeight: 800, fontFamily: 'monospace' }} /></Tooltip>
            </Box>
          </Box>
          {isExpanded && <Box id={`study-open-details-${item.event_id}`}><SeriesDetails item={item} maxMs={maxMs} /></Box>}
        </React.Fragment>;
      })}
      </Box>
    </Box>
  );
};

export default StudyPerformanceMonitoring;
